// Copyright (c) StrangeBee 2024

import { classList } from '@Helpers/className';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import THSelectorItem from './THSelectorItem';
import useDebounce from '@Hooks/useDebounce';
import { useLocalMessage } from '@Helpers/locale';
import { produce, enableMapSet } from 'immer';
import { Spin } from 'antd';
import isEqual from 'lodash.isequal';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';

import './THSelector.scss';

enableMapSet();

// TODO: Refacto from scratch this component (thx jj)

const remove =
    (item: any) =>
    (e: any): boolean =>
        e !== item;

const toggle = <T,>(prev: Set<T>, value: T): Set<T> => {
    // ! /!\ compare wih reference, not pure values
    if (prev.has(value)) {
        prev.delete(value);
    } else {
        prev.add(value);
    }
    return prev;
};

interface THSelectorProps<T> {
    dataSource: T[];
    onClick?: (item: T, value?: T[]) => void;
    refKey?: keyof T;
    filtering?: {
        search: (item: T) => string;
        placeholder?: string;
    };
    single?: boolean;
    children: (item: T, value?: boolean, index?: number) => ReactElement;
    value?: T[];
    onChange?: (value: T[], itemAdded?: T) => void;
    className?: string;
    displayEmpty?: ReactElement;
    disabled?: boolean;
    loading?: boolean;
    customSelect?: (item: T, value: T[]) => boolean;
    showSelected?: boolean;
    watchValue?: boolean;
}

interface THSelectorFilter<T> {
    input: React.ReactElement;
    matchValue: (input: string, item: T) => boolean;
}
type THSelectorWithFiltersProps<T> = Omit<THSelectorProps<T>, 'filtering'> & { filters: THSelectorFilter<T>[] };

const THSelector = <T,>(props: THSelectorProps<T>): ReactElement => {
    const { filtering } = props;
    const filters: THSelectorFilter<T>[] = [];
    if (filtering) {
        filters.push({
            input: <THInput className="th-selector__filters_input" placeholder={filtering.placeholder} errorless />,
            matchValue: (input, item) => {
                const itemSearch = filtering.search(item);
                return itemSearch.includes(input);
            },
        });
    }
    return <THSelectorWithFilters {...props} filters={filters} />;
};

export const THSelectorWithFilters = <T,>({
    dataSource,
    onClick,
    refKey,
    filters,
    single = false,
    value = [],
    onChange,
    children,
    className,
    displayEmpty,
    disabled = false,
    showSelected = false,
    loading: forceLoading = false,
    customSelect,
    watchValue,
}: THSelectorWithFiltersProps<T>): ReactElement => {
    const i = useLocalMessage();

    const [filterInputs, setFilterInputs] = useState<(string | null)[]>(
        filters.map(() => {
            return null;
        }),
    );
    const [loading, setLoading] = useState(false);
    const [modeSelected, setModeSelected] = useState(false);
    const [selectedList, setSelectedList] = useState(new Set(value));

    const { debounce } = useDebounce();

    const [dataSourceSearched, setDataSourceSearched] = useState(dataSource);

    const onClickHandler = useCallback(
        (item: T) => (): void => {
            const selected = customSelect?.(item, value) || selectedList.has(item);

            setSelectedList(
                produce((draft) => {
                    toggle<T>(draft as Set<T>, item);
                }),
            );

            if (!onChange) {
                onClick?.(item);
                return;
            }

            const list = single ? [] : value;
            const newValues = selected ? value?.filter(remove(item)) : [...list, item];

            onClick?.(item, newValues);
            onChange(newValues, item);
        },
        [onChange, onClick, single, value, selectedList, customSelect],
    );

    const detectSelected: (item: T, value: T[]) => boolean = useCallback(
        (item, value) => {
            const hasItem = Array.from(selectedList).some((value) => isEqual(item, value));

            return !customSelect ? hasItem : customSelect(item, value);
        },
        [customSelect, selectedList],
    );

    const fetchValue = useCallback(() => {
        let dataFound = dataSource;
        filters.forEach((filter, idx) => {
            const filterInput = filterInputs[idx];
            if (filterInput) {
                dataFound = dataFound.filter((item) => filter.matchValue(filterInput, item));
            }
        });
        setLoading(false);
        setDataSourceSearched(dataFound);
    }, [dataSource, filters, filterInputs]);

    const onFilterChange = useCallback(
        (change: string, idx: number): void => {
            setLoading(true);
            setModeSelected(false);
            filterInputs[idx] = change;
            setFilterInputs(filterInputs);
            debounce({
                onTimeout: () => fetchValue(),
                timeoutDelay: 100,
            });
        },
        [debounce, fetchValue, filterInputs],
    );

    const onClickSelected = useCallback(() => {
        const dataFound = modeSelected ? dataSource : dataSource.filter((item) => selectedList.has(item));
        setDataSourceSearched(dataFound);
        setModeSelected(!modeSelected);
    }, [modeSelected, dataSource, selectedList]);

    useEffect(() => {
        if (dataSource.length) {
            fetchValue();
        }
        if (filters[0]?.input.props.defaultValue) {
            onFilterChange(filters[0].input.props.defaultValue, 0);
        }
        if (filters[1]?.input.props.defaultValue) {
            onFilterChange(filters[1].input.props.defaultValue, 1);
        }
        //selectedList.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource]);

    useEffect(() => {
        if (watchValue) {
            setSelectedList(new Set(value));
        }
    }, [value, watchValue]);

    if (forceLoading) {
        return <Spin />;
    }

    if (displayEmpty && dataSource.length === 0) {
        return <div className={classList('th-selector', 'th-selector__empty', className)}>{displayEmpty}</div>;
    }

    return (
        <div className={classList('th-selector', { disabled })}>
            {filters.length > 0 && !modeSelected ? (
                <div className={classList('th-selector__filters')}>
                    {filters.map((filter, idx) => {
                        let onChange = (event: any) => onFilterChange(event.target.value, idx);
                        if (filter.input.type == THSelect) {
                            onChange = (v) => onFilterChange(v, idx);
                        }

                        return React.cloneElement(filter.input, {
                            onChange,
                            key: `th-selector__filters_${idx}`,
                            errorless: true,
                        });
                    })}
                </div>
            ) : null}
            <div className={classList('th-selector__wrappper', { loading: loading || forceLoading })}>
                <div className={classList('th-selector__header', { show: showSelected && selectedList.size })}>
                    <span onClick={onClickSelected} className="th-selector__btn">
                        {!modeSelected ? i('common.show-selection') : i('common.show-all')}
                    </span>
                </div>
                <ul className={classList('th-selector__list', className)}>
                    {dataSourceSearched.map((item, index) => (
                        <THSelectorItem
                            key={refKey ? (item[refKey] as any).toString() : JSON.stringify(item)}
                            index={index}
                            item={item}
                            onClickHandler={!disabled ? onClickHandler : undefined}
                            selected={detectSelected(item, value)}
                        >
                            {children}
                        </THSelectorItem>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default THSelector;
